import { useEffect } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import WideCardLoader from '@/modules/content/components/ContentCard/Wide/Loader';
import { useCourse } from '@/modules/corporate/modules/course/courseContext';
import { QuizDetailsContentFragment } from '@/modules/corporate/modules/quiz/views/QuizDetails/graphql/fragments/generated/QuizDetailsContent';
import { QuizDetailsPerformanceFragment } from '@/modules/corporate/modules/quiz/views/QuizDetails/graphql/fragments/generated/QuizDetailsPerformance';
import { useQuizDetailsQuery } from '@/modules/corporate/modules/quiz/views/QuizDetails/graphql/queries/generated/quizDetails';
import { useQuizDetailsPerformanceQuery } from '@/modules/corporate/modules/quiz/views/QuizDetails/graphql/queries/generated/quizDetailsPerformance';

import InlineQuizContainer from '../InlineQuizContainer';
import PopupQuizContainer from '../PopupQuizContainer';
import { QuizContainerProps } from './types';

const QuizContainer: React.FC<QuizContainerProps> = (props) => {
  useTranslation('QuizPlayer');
  const { contentId, courseId, userId, onLoad, onQuizSubmit, onEditQuiz, inlinePlayer } = props;
  const { course } = useCourse();
  const { isLocalizedContent, defaultLanguageType } = course;
  const { isMobile } = useScreen();
  const user = useUserContext();

  const [{ fetching: fetchingContent, data }] = useQuizDetailsQuery({
    variables: {
      id: contentId,
      isEmbed: true,
      languageType: course.languageType ?? defaultLanguageType,
    },
  });
  const [{ fetching, data: performanceData }] = useQuizDetailsPerformanceQuery({
    variables: { contentId, userId: user.id, parentEntityId: course.id, isLocalizedContent },
  });

  const content = data?.content as QuizDetailsContentFragment | null;
  const quizPerformance = performanceData?.quizPerformances?.edges?.[0]?.node as
    | QuizDetailsPerformanceFragment
    | undefined;

  // TODO: Quiz remove duplicate in native
  useEffect(() => {
    if (content) {
      onLoad?.(content);
    }
  }, [content, onLoad]);

  const result = quizPerformance?.maxSubmissionDetails?.result;
  const performanceStatus = quizPerformance?.status;
  useEffect(() => {
    if (result || performanceStatus) {
      onQuizSubmit?.({ performanceStatus, result: result ?? undefined });
    }
  }, [onQuizSubmit, performanceStatus, result]);

  if (fetchingContent || fetching) {
    return <WideCardLoader className="py-60" />;
  }

  if (!content) {
    return null;
  }

  if (inlinePlayer && !onEditQuiz) {
    return (
      <InlineQuizContainer
        contentId={contentId}
        courseId={courseId}
        isLocalizedContent={isLocalizedContent}
        quiz={content}
        quizPerformance={quizPerformance}
        userId={userId}
        onQuizSubmit={onQuizSubmit}
      />
    );
  }

  return (
    <PopupQuizContainer
      contentId={contentId}
      courseId={courseId}
      isLocalizedContent={isLocalizedContent}
      quiz={content}
      quizPerformance={quizPerformance}
      userId={userId}
      onEditQuiz={onEditQuiz}
      onQuizSubmit={onQuizSubmit}
    />
  );
};

export default QuizContainer;
